import { createContext, useContext, useEffect, useRef, useState } from "react";

import type { Breakpoint, BreakpointState } from "./useBreakpoint";

/**
 * Container-based breakpoint state — identical shape to BreakpointState
 * but derived from a container element's width, not the window.
 */

/**
 * Context that provides container breakpoint state to descendant blocks.
 * When no provider is present, consumers fall back to window-based
 * useBreakpoint (see useContainerBreakpoint hook).
 */
const ContainerBreakpointContext = createContext<BreakpointState | null>(null);

export { ContainerBreakpointContext };

/**
 * useContainerBreakpoint — measures a container element's width and returns
 * the same BreakpointState shape as useBreakpoint, but derived from the
 * container, not the window.
 *
 * Blocks that need to respond to their render width (not the viewport) should
 * use this hook instead of useBreakpoint. When rendered inside a
 * ContainerQueryProvider, the hook reads from context (no ref needed). When
 * no provider is present, it falls back to measuring its own ref, or
 * ultimately to window-based breakpoints.
 *
 * @returns BreakpointState — same shape as useBreakpoint
 */
export function useContainerBreakpoint(containerRef?: React.RefObject<HTMLElement | null>): BreakpointState {
  const ctx = useContext(ContainerBreakpointContext);
  const ref = useRef<HTMLElement | null>(null);
  const target = containerRef ?? ref;
  const [state, setState] = useState<BreakpointState>(() => {
    if (ctx) return ctx;
    // Default to desktop. ContainerQueryProvider will override with the
    // measured width on mount. This avoids a flash of mobile layout and
    // matches the pre-container-query behavior where matchMedia returned
    // matches: false (desktop) in tests.
    return {
      current: "xl",
      isSm: true,
      isMd: true,
      isLg: true,
      isXl: true,
      is2Xl: false,
      isMobile: false,
      isDesktop: true,
    };
  });

  useEffect(() => {
    // If we have context, use it — no need to observe.
    if (ctx) {
      setState(ctx);
      return;
    }

    const el = target.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      const width = el.offsetWidth;
      const isSm = width >= 640;
      const isMd = width >= 768;
      const isLg = width >= 1024;
      const isXl = width >= 1280;
      const is2Xl = width >= 1536;

      let current: Breakpoint | "base" = "base";
      if (is2Xl) current = "2xl";
      else if (isXl) current = "xl";
      else if (isLg) current = "lg";
      else if (isMd) current = "md";
      else if (isSm) current = "sm";

      setState({
        current,
        isSm,
        isMd,
        isLg,
        isXl,
        is2Xl,
        isMobile: !isMd,
        isDesktop: isMd,
      });
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ctx, target]);

  return state;
}
